import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { appliedFiltersType } from "../../../../services/api/types/reports/reports";
import { formatKey } from "../utils/helpers";

interface ExpandableFiltersSectionProps {
  appliedFilters: appliedFiltersType;
  expanded?: boolean;
  onExpandedChange?: (v: boolean) => void;
}

export const ExpandableFiltersSection: React.FC<
  ExpandableFiltersSectionProps
> = ({ appliedFilters, expanded, onExpandedChange }) => {
  const [internalExpanded, setIsInternalExpanded] = useState(true);

  const { general, userAttributes, assignments } = appliedFilters;

  const userActivityDate =
    general?.UserActivityDate ||
    [general?.UserActivityDateFrom, general?.UserActivityDateTo]
      .filter(Boolean)
      .join(" - ");

  const hasUserAttributes =
    userAttributes && Object.keys(userAttributes).length > 0;
  const hasAssignmentFilters =
    assignments && Object.keys(assignments).length > 0;

  const isExpanded = expanded !== undefined ? expanded : internalExpanded;

  return (
    <Accordion
      elevation={0}
      expanded={isExpanded}
      onChange={() => {
        setIsInternalExpanded((prev) => !prev);
        onExpandedChange?.(!isExpanded);
      }}
      sx={{
        backgroundColor: "#F9FAFB",
        borderRadius: "12px",
        mb: 3,
        border: "1px solid #E5E7EB",
        boxShadow: "0px 1px 3px 0px #1018281A",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 3,
          py: 2,
          minHeight: 72,
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
        }}
      >
        <Box>
          <Typography
            sx={{ color: "#101828", fontSize: "14px", fontWeight: 600 }}
          >
            Applied Report Filters
          </Typography>
          <Typography
            sx={{ color: "#475467", fontSize: "12px", fontWeight: 400 }}
          >
            This report reflects the following filter configuration
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: "0 24px 16px 24px" }}>
        <Box>
          <Divider sx={{ mb: 1 }} />
          <Stack
            direction="row"
            sx={{ flexWrap: "wrap" }}
            columnGap={6}
            rowGap={1.5}
          >
            <Box>
              <Typography fontWeight={600} color="#475467" fontSize="12px">
                Portal:
              </Typography>
              <Typography color="#344054" fontSize="12px">
                {general?.Portal}
              </Typography>
            </Box>
            <Box>
              <Typography fontWeight={600} color="#475467" fontSize="12px">
                Group results by portal:
              </Typography>
              <Typography color="#344054" fontSize="12px">
                {general?.GroupByPortal}
              </Typography>
            </Box>
            {general?.UserActivity && (
              <Box>
                <Typography fontWeight={600} color="#475467" fontSize="12px">
                  User activity type:
                </Typography>
                <Typography color="#344054" fontSize="12px">
                  {general?.UserActivity}
                </Typography>
              </Box>
            )}
            {userActivityDate && (
              <Box>
                <Typography fontWeight={600} color="#475467" fontSize="12px">
                  User activity date:
                </Typography>
                <Typography color="#344054" fontSize="12px">
                  {userActivityDate}
                </Typography>
              </Box>
            )}
          </Stack>

          {hasUserAttributes && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography
                fontSize="12px"
                color="#475467"
                fontWeight={600}
                mb={1.5}
              >
                User attributes
              </Typography>
              <Stack
                direction="row"
                columnGap={6}
                rowGap={1.5}
                sx={{ flexWrap: "wrap" }}
              >
                {Object.entries(userAttributes).map(([key, value]) => (
                  <Box key={key}>
                    <Typography
                      fontWeight={600}
                      color="#475467"
                      fontSize="12px"
                    >
                      {formatKey(key)}:
                    </Typography>
                    <Typography color="#344054" fontSize="12px">
                      {value || "/"}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}

          {hasAssignmentFilters && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography
                fontSize="12px"
                color="#475467"
                fontWeight={600}
                mb={1.5}
              >
                Assignments
              </Typography>
              <Stack
                direction="row"
                columnGap={6}
                rowGap={1.5}
                sx={{ flexWrap: "wrap" }}
              >
                {Object.entries(assignments).map(([key, value]) => (
                  <Box key={key}>
                    <Typography
                      fontWeight={600}
                      color="#475467"
                      fontSize="12px"
                    >
                      {formatKey(key)}:
                    </Typography>
                    <Typography color="#344054" fontSize="12px">
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
